/**
 * The Studio sidebar: grouped by area (docs/roles-and-permissions.md) and filtered by the capabilities of the person
 * signed in. Each item names the capabilities that show it (any of them). `g` + key jumps to an item; keys are unique
 * across all groups (test/nav.test.ts).
 */
export interface NavItem {
  to: string;
  label: string;
  /** Shortcut after `g`. */
  key?: string;
  /** Shown when the person holds any of these capabilities; none = everyone. */
  anyOf?: readonly string[];
  /** Not built yet: shown greyed out with this note. */
  placeholder?: string;
}

export interface NavGroup {
  id: string;
  /** No label = always open, no heading (Today). */
  label?: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: 'today', items: [{ to: '/', label: 'Today', key: 't' }] },
  {
    id: 'content',
    label: 'Content',
    items: [
      { to: '/review', label: 'Review', key: 'r', anyOf: ['content.review:read'] },
      { to: '/coupons', label: 'Coupons', key: 'c', anyOf: ['content.coupons:read'] },
      { to: '/add-coupons', label: 'Add coupons', key: 'j', anyOf: ['content.coupons:write'] },
      { to: '/coverage', label: 'Coverage', key: 'g', anyOf: ['content.coupons:read'] },
      { to: '/merchants', label: 'Merchants', key: 'm', anyOf: ['content.merchants:read'] },
      { to: '/categories', label: 'Categories', key: 'y', anyOf: ['content.categories:read'] },
      { to: '/markets', label: 'Markets', key: 'w', anyOf: ['content.merchants:read'] },
      { to: '/campaign-pages', label: 'Campaign pages', key: 'q', anyOf: ['content.pages:read'] },
      { to: '/widgets', label: 'Widgets', key: 'z', anyOf: ['content.pages:read'] },
      { to: '/import', label: 'Import', key: 'i', anyOf: ['content.import:read'] },
    ],
  },
  {
    id: 'sales',
    label: 'Partners & Sales',
    items: [
      { to: '/brand-deals', label: 'Brand deals', anyOf: ['sales.deals:read'], placeholder: 'coming next' },
      { to: '/sponsored', label: 'Sponsored exclusives', key: 'b', anyOf: ['sales.sponsored:read'] },
      { to: '/revenue', label: 'Revenue', key: 'v', anyOf: ['insights.revenue:read'] },
    ],
  },
  {
    id: 'affiliate',
    label: 'Affiliate',
    items: [
      { to: '/networks', label: 'Networks', key: 'n', anyOf: ['affiliate.setup:read'] },
      { to: '/network-accounts', label: 'Network accounts', key: 'o', anyOf: ['affiliate.setup:read'] },
      { to: '/programs', label: 'Programs', key: 'p', anyOf: ['affiliate.setup:read'] },
      { to: '/deeplinks', label: 'Deeplinks', key: 'd', anyOf: ['affiliate.setup:read'] },
      { to: '/links', label: 'Links', key: 'l', anyOf: ['affiliate.links:read'] },
    ],
  },
  {
    id: 'sources',
    label: 'Sources',
    items: [
      { to: '/competitor-sources', label: 'Competitor sources', key: 'f', anyOf: ['sources.competitors:read'] },
      { to: '/newsletters', label: 'Newsletters', key: 'e', anyOf: ['sources.newsletters:read'] },
      { to: '/api-health', label: 'API health', key: 'x', anyOf: ['sources.apiHealth:read'] },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { to: '/sites', label: 'Sites', key: 's', anyOf: ['admin.sites:read'] },
      { to: '/themes', label: 'Themes', key: 'h', anyOf: ['admin.themes:read'] },
      { to: '/users', label: 'People', key: 'u', anyOf: ['admin.people:read'] },
      { to: '/api-keys', label: 'API keys', key: 'k', anyOf: ['admin.apiKeys:read'] },
      { to: '/audit', label: 'Audit log', key: 'a', anyOf: ['admin.audit:read'] },
    ],
  },
];

/** Shortcut keys taken by entries on other branches, kept free here. */
export const RESERVED_NAV_KEYS: readonly string[] = [];

export function canSee(item: NavItem, capabilities: ReadonlySet<string>): boolean {
  return !item.anyOf || item.anyOf.some((c) => capabilities.has(c));
}

/** Groups with only the items this person may open; empty groups disappear. */
export function visibleNav(capabilities: Iterable<string>, groups: readonly NavGroup[] = NAV_GROUPS): NavGroup[] {
  const held = new Set(capabilities);
  return groups.map((g) => ({ ...g, items: g.items.filter((item) => canSee(item, held)) })).filter((g) => g.items.length > 0);
}

export function navItems(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items);
}

const COLLAPSED_KEY = 'studio.nav.collapsed';

/** Collapsed group ids, remembered per browser; storage can be missing or blocked, so every access is guarded. */
export function readCollapsed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeCollapsed(ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids]));
  } catch {
    // Private mode or blocked storage: the sidebar just forgets.
  }
}
